/**
 * Controller para la página de eventos públicos.
 *
 * Responsabilidades:
 * - Muestra/oculta skeleton mientras se carga el frame lessons_list
 * - Sincroniza estado visual del datepicker y selector de servicio (no se recargan)
 * - Actualiza la URL con history.replaceState al cambiar fecha/servicio
 * - Navega al frame lessons_list al cambiar el selector de servicio
 *
 * Flujo: Los links del datepicker y el selector de servicio targetean events_content.
 * Al reemplazar el frame completo, el datepicker actualiza los puntitos por servicio
 * y los links conservan el servicio seleccionado. Metadata en data-events-meta para sync/URL.
 */
import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const EVENTS_FRAME_ID = "events_content"
const DEFAULT_SERVICE = "todos"

// Deben coincidir con app/views/events/_datepicker.html.erb
const DATEPICKER_CLASSES = {
  selected: "border-button-primary-bg text-button-primary-bg",
  selectable:
    "border-primary-light-color/30 bg-background hover:border-button-primary-bg/60 hover:bg-button-primary-bg/10 text-primary-color active:scale-[0.97] active:opacity-90 transition-[transform,opacity,colors] duration-150 motion-reduce:active:scale-100 touch-manipulation",
  disabled: "border-background-light/50 bg-background/50 text-primary-light-color/60 cursor-default transition-colors"
}

const ALL_DATEPICKER_CLASSES = [
  ...DATEPICKER_CLASSES.selected.split(" "),
  ...DATEPICKER_CLASSES.selectable.split(" "),
  ...DATEPICKER_CLASSES.disabled.split(" ")
]

export default class extends Controller {
  static targets = ["skeleton", "serviceSelectContainer"]
  static values = { basePath: String }

  connect() {
    this.boundShowSkeleton = this.showSkeleton.bind(this)
    this.boundHideSkeletonAndSync = this.hideSkeletonAndSync.bind(this)
    this.boundHideSkeleton = this.hideSkeleton.bind(this)

    document.documentElement.addEventListener("turbo:before-fetch-request", this.boundShowSkeleton)
    document.documentElement.addEventListener("turbo:frame-load", this.boundHideSkeletonAndSync)
    document.documentElement.addEventListener("turbo:fetch-request-error", this.boundHideSkeleton)
  }

  disconnect() {
    document.documentElement.removeEventListener("turbo:before-fetch-request", this.boundShowSkeleton)
    document.documentElement.removeEventListener("turbo:frame-load", this.boundHideSkeletonAndSync)
    document.documentElement.removeEventListener("turbo:fetch-request-error", this.boundHideSkeleton)
  }

  // --- Turbo frame: skeleton y sync ---

  showSkeleton(event) {
    if (!this.hasSkeletonTarget) return
    const header = this._turboFrameHeaderFromEvent(event)
    const matchesHeader = header === EVENTS_FRAME_ID
    const matchesTarget = !header && this._isEventsListRequest(event.target)
    if (!matchesHeader && !matchesTarget) return
    this.skeletonTarget.classList.remove("hidden")
    this._syncFromUrl(event.detail?.url)
  }

  _turboFrameHeaderFromEvent(event) {
    const headers = event.detail?.fetchOptions?.headers
    if (!headers) return null
    if (typeof headers.get === "function") return headers.get("Turbo-Frame")
    return headers["Turbo-Frame"]
  }

  hideSkeleton(event) {
    if (event.target?.id !== EVENTS_FRAME_ID || !this.hasSkeletonTarget) return
    this.skeletonTarget.classList.add("hidden")
  }

  hideSkeletonAndSync(event) {
    this.hideSkeleton(event)
    this._syncFromFrame(event.target)
  }

  // --- Navegación: cambio de servicio ---

  navigateOnServiceChange(event) {
    if (!this.hasServiceSelectContainerTarget || !this.hasBasePathValue) return
    const date = this.serviceSelectContainerTarget.dataset.date
    if (!date) return

    const url = this._buildEventsUrl(date, event.target.value)
    Turbo.visit(url, { frame: EVENTS_FRAME_ID })
  }

  // --- Sync de controles desde URL o frame ---

  _syncFromUrl(url) {
    if (!url) return
    const params = new URL(url.toString()).searchParams
    const date = params.get("date")
    const service = params.get("service") || DEFAULT_SERVICE
    if (date) this._updateSelectors(date, service)
  }

  _syncFromFrame(frame) {
    if (frame?.id !== EVENTS_FRAME_ID) return
    const meta = frame.querySelector("[data-events-meta]")
    const date = meta?.dataset?.date
    const service = meta?.dataset?.service || DEFAULT_SERVICE
    if (date) this._updateSelectors(date, service)
  }

  _updateSelectors(date, service) {
    this._updateDatepicker(date)
    this._updateServiceSelect(date, service)
    this._updateHistoryState(date, service)
  }

  _updateDatepicker(date) {
    this.element.querySelectorAll("[data-date]").forEach((card) => {
      card.classList.remove(...ALL_DATEPICKER_CLASSES)
      const isSelected = card.dataset.date === date
      if (isSelected) {
        card.classList.add(...DATEPICKER_CLASSES.selected.split(" "))
      } else if (card.tagName === "A") {
        card.classList.add(...DATEPICKER_CLASSES.selectable.split(" "))
      } else {
        card.classList.add(...DATEPICKER_CLASSES.disabled.split(" "))
      }
    })
  }

  _updateServiceSelect(date, service) {
    if (!this.hasServiceSelectContainerTarget) return
    this.serviceSelectContainerTarget.dataset.date = date
    const select = document.getElementById("events_service_select")
    if (select?.value !== service) select.value = service
  }

  _updateHistoryState(date, service) {
    if (!this.hasBasePathValue || !date) return
    const url = this._buildEventsUrl(date, service)
    window.history.replaceState({ turboFrame: EVENTS_FRAME_ID }, "", url)
  }

  // --- Helpers ---

  _isEventsListRequest(target) {
    return target?.id === EVENTS_FRAME_ID || target?.dataset?.turboFrame === EVENTS_FRAME_ID
  }

  _buildEventsUrl(date, service) {
    const url = new URL(this.basePathValue, window.location.origin)
    url.searchParams.set("date", date)
    url.searchParams.set("service", service || DEFAULT_SERVICE)
    return url.toString()
  }
}
